import { createNode } from '@glueapp/graph-types';

export const FilterList = createNode({
    id: 'Filter List',
    name: 'Filter List',
    description:
        'Filter items from a list using a predicate function. The function receives each item and returns true to keep it or false to filter it out.',
    aiDescription: [
        'Filters a list by running a predicate function on each item. Async — uses executeFunctionNode.',
        'Setup steps:',
        '1. Create a function with inputs "Item" (type T), "Index" (number), "List" (array of T) and output "Keep" (boolean).',
        '   Inside the function, build logic that outputs true to keep the item or false to discard it.',
        '2. Add this node.',
        '3. Connect a list to "Input List".',
        '4. Bind the function to the "Predicate" port (kind: function) by SETTING its value to a function reference (not a connection): set_value <thisNodeId> "Predicate" {"isFunctionReferenceValue":true,"functionId":"<functionId>"}.',
        'Outputs: "Result" (filtered array), "Kept Count" (number), "Filtered Count" (number).',
    ].join('\n'),
    category: 'List',
    color: '#778ed5',
    isAsync: true,

    inPorts: {
        'Input List': {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'T',
            },
        },
        Predicate: {
            kind: 'function',
            inputTypes: {
                Item: { kind: 'generic', identifier: 'T' },
                Index: { kind: 'number' },
                List: {
                    kind: 'array',
                    subType: { kind: 'generic', identifier: 'T' },
                },
            },
            outputTypes: {
                Keep: { kind: 'boolean' },
            },
        },
    },

    outPorts: {
        Result: {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'T',
            },
        },
        'Kept Count': {
            kind: 'number',
        },
        'Filtered Count': {
            kind: 'number',
        },
    },

    execute: async ({ inPorts, executeFunctionNode }) => {
        if (!executeFunctionNode) {
            throw new Error('Function execution not available');
        }

        const list = inPorts['Input List'] as any[];
        const predicateValue = inPorts['Predicate'];

        // Check if the predicate is a FunctionReferenceValue
        if (
            !predicateValue ||
            typeof predicateValue !== 'object' ||
            !('isFunctionReferenceValue' in predicateValue) ||
            !predicateValue.isFunctionReferenceValue
        ) {
            throw new Error('Predicate must be a function reference');
        }

        const predicate = predicateValue as any; // Type as FunctionReferenceValue

        if (!list || !Array.isArray(list)) {
            return {
                output: {
                    Result: [],
                    'Kept Count': 0,
                    'Filtered Count': 0,
                },
            };
        }

        const keptItems = [];

        for (let i = 0; i < list.length; i++) {
            const item = list[i];

            // Execute the predicate function using the function ID from the reference
            const result = await executeFunctionNode(predicate.functionId, {
                Item: item,
                Index: i,
                List: list,
            });

            const shouldKeep = Boolean(result.output.Keep);

            if (shouldKeep) {
                keptItems.push(item);
            }
        }

        return {
            output: {
                Result: keptItems,
                'Kept Count': keptItems.length,
                'Filtered Count': list.length - keptItems.length,
            },
        };
    },
});
